import { type AttachmentId, type LocationId, toResponseError } from '@terros/common'
import { useMutation } from '@tanstack/react-query'
import { useAddAttachment } from './add'

type UploadAttachmentInput = {
  file: File
  locationId?: LocationId
}

type UseUploadAttachmentResult = {
  upload: (input: UploadAttachmentInput) => Promise<AttachmentId>
  isUploading: boolean
  error: string | null
}

export function useUploadAttachment(): UseUploadAttachmentResult {
  const { add, isAdding } = useAddAttachment()

  const mutation = useMutation({
    mutationFn: async ({ file, locationId }: UploadAttachmentInput): Promise<AttachmentId> => {
      try {
        const { attachment, uploadUrl } = await add({
          filename: file.name,
          contentType: file.type,
          size: file.size,
          locationId,
        })

        await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type,
            'If-None-Match': '*',
          },
        })

        return attachment.attachmentId
      } catch (error) {
        throw toResponseError(error)
      }
    },
  })

  return {
    upload: mutation.mutateAsync,
    isUploading: mutation.isPending || isAdding,
    error: mutation.error?.message ?? null,
  }
}
